import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Calendar, 
  CheckSquare, 
  AlertTriangle, 
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Zap,
  Users,
  Plus,
  ArrowRight
} from 'lucide-react';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameDay, 
  isToday,
  addWeeks,
  subWeeks,
  isBefore,
  parseISO
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { useOptimizedEvents } from '@/hooks/useOptimizedEvents';
import { useOptimizedTasks } from '@/hooks/useOptimizedTasks';
import { useOptimizedFamily } from '@/hooks/useOptimizedFamily';
import { useTaskLists } from '@/hooks/useTaskLists';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { PriorityIndicator } from './PriorityIndicator';
import { QuickActions } from './QuickActions';
import { EventWithTasks } from './EventWithTasks';
import { useDashboardData } from '@/hooks/useDashboardData';

interface WeekItem {
  id: string;
  type: 'event' | 'task';
  title: string;
  date: Date;
  time?: string;
  priority: number;
  isOverdue?: boolean;
  hasConflict?: boolean;
  linkedPersons?: string[];
  assignedTo?: string[];
  event_id?: string;
}

interface DayData {
  date: Date;
  items: WeekItem[];
  conflicts: WeekItem[][];
  totalPriority: number;
}

export function SmartWeekView() {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showQuickActions, setShowQuickActions] = useState(false);
  const { family } = useOptimizedFamily();
  
  // Pre-fetch all dashboard data in a single RPC call
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboardData(family?.id);
  
  // Use pre-fetched data from cache (populated by useDashboardData)
  const { events } = useOptimizedEvents(family?.id);
  const { tasks } = useOptimizedTasks(family?.id);
  const { taskLists } = useTaskLists(family?.id);
  const { openModal } = useModalNavigation();
  
  // Show loading state while dashboard data is being fetched
  const isLoading = dashboardLoading && !dashboardData;

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Collect all items for this week
  const weekItems: WeekItem[] = [];

  // Add events
  if (events) {
    events.forEach(event => {
      const eventStartDate = new Date(event.start_date);
      const eventEndDate = new Date(event.end_date);
      
      // Check if event overlaps with current week
      if (eventEndDate < weekStart || eventStartDate > weekEnd) {
        return; // Event doesn't overlap with this week, skip it
      }
      
      // Check if it's a multi-day event
      const isMultiDay = eventStartDate.toDateString() !== eventEndDate.toDateString();
      
      if (isMultiDay) {
        // For multi-day events, add the event to each day it spans within this week
        // Clamp the interval to only show days within the current week
        const intervalStart = eventStartDate > weekStart ? eventStartDate : weekStart;
        const intervalEnd = eventEndDate < weekEnd ? eventEndDate : weekEnd;
        
        const eventDays = eachDayOfInterval({ 
          start: intervalStart, 
          end: intervalEnd 
        });
        
        eventDays.forEach(day => {
          const eventTime = format(eventStartDate, 'HH:mm');
          weekItems.push({
            id: event.id + '-' + format(day, 'yyyy-MM-dd'), // Unique ID for each day occurrence
            type: 'event',
            title: event.title,
            date: day,
            time: eventTime === '00:00' ? undefined : eventTime,
            priority: calculateEventPriority(event, day),
            linkedPersons: event.linked_persons || []
          });
        });
      } else {
        // Single day event
        if (eventStartDate >= weekStart && eventStartDate <= weekEnd) {
          const eventTime = format(eventStartDate, 'HH:mm');
          weekItems.push({
            id: event.id,
            type: 'event',
            title: event.title,
            date: eventStartDate,
            time: eventTime === '00:00' ? undefined : eventTime,
            priority: calculateEventPriority(event, eventStartDate),
            linkedPersons: event.linked_persons || []
          });
        }
      }
    });
  }

  // Add tasks
  if (tasks) {
    tasks.forEach(task => {
      if (task.due_date && task.status !== 'completed') {
        const taskDate = new Date(task.due_date + 'T00:00:00');
        if (taskDate >= weekStart && taskDate <= weekEnd) {
          weekItems.push({
            id: task.id,
            type: 'task',
            title: task.title,
            date: taskDate,
            priority: calculateTaskPriority(task, taskDate),
            isOverdue: isBefore(taskDate, new Date()),
            assignedTo: task.assigned_to_list || [task.assigned_to],
            event_id: task.event_id
          });
        }
      }
    });
  }

  // Group items by day and detect conflicts
  const dayData: DayData[] = weekDays.map(date => {
    const dayItems = weekItems.filter(item => isSameDay(item.date, date));
    
    // Sort today's items by time, then by priority
    if (isToday(date)) {
      dayItems.sort((a, b) => {
        if (a.time && b.time) {
          return a.time.localeCompare(b.time);
        }
        if (a.time && !b.time) return -1;
        if (!a.time && b.time) return 1;
        return b.priority - a.priority;
      });
    }
    
    const conflicts = detectTimeConflicts(dayItems);
    const totalPriority = dayItems.reduce((sum, item) => sum + item.priority, 0);
    
    return {
      date,
      items: dayItems,
      conflicts,
      totalPriority
    };
  });

  const totalConflicts = dayData.reduce((sum, day) => sum + day.conflicts.length, 0);
  const highPriorityDays = dayData.filter(day => day.totalPriority >= 8).length;

  // Separate days into past, today, and future
  const today = new Date();
  
  // A day is considered "past" only if it's before today AND has no ongoing events
  const pastDays = dayData.filter(day => {
    if (!isBefore(day.date, today) || isToday(day.date)) return false;
    
    // Check if this day has any events that end in the future
    const hasOngoingEvents = day.items.some(item => {
      if (item.type !== 'event') return false;
      
      // Find the actual event to check its end_date
      const event = events?.find(e => {
        // Handle multi-day events with modified IDs
        const originalId = item.id.includes('-2024-') || item.id.includes('-2025-') || item.id.includes('-2026-') ? 
          item.id.split('-')[0] + '-' + item.id.split('-')[1] + '-' + item.id.split('-')[2] + '-' + item.id.split('-')[3] + '-' + item.id.split('-')[4] :
          item.id;
        return e.id === originalId;
      });
      
      if (!event) return false;
      
      const eventEndDate = new Date(event.end_date);
      return eventEndDate >= today;
    });
    
    return !hasOngoingEvents;
  });
  
  const todayData = dayData.find(day => isToday(day.date));
  const futureDays = dayData.filter(day => !isBefore(day.date, today) && !isToday(day.date));
  
  // Count total activities in past days
  const pastActivitiesCount = pastDays.reduce((sum, day) => sum + day.items.length, 0);

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeek(direction === 'prev' ? subWeeks(currentWeek, 1) : addWeeks(currentWeek, 1));
  };

  // Show skeleton loading when data is not ready
  if (isLoading) {
    return (
      <div className="w-full pb-16">
        <div className="pb-1">
          <div className="flex items-center justify-between">
            <div className="h-6 w-40 bg-muted animate-pulse rounded" />
          </div>
          <div className="flex items-center justify-between w-full mt-3">
            <div className="h-8 w-8 bg-muted animate-pulse rounded" />
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
            <div className="h-8 w-8 bg-muted animate-pulse rounded" />
          </div>
        </div>
        <div className="space-y-2 mt-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="w-full p-3 rounded-lg border border-panel bg-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-muted animate-pulse rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                  <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-16">
      <div className="pb-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Vue hebdomadaire</h2>
          </div>
        </div>
        <div className="flex items-center justify-between w-full mt-3">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => navigateWeek('prev')}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center justify-between flex-1 mx-4">
            <span className="text-sm text-muted-foreground">
              {format(weekStart, 'd MMM', { locale: fr })} - {format(weekEnd, 'd MMM', { locale: fr })}
            </span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => navigateWeek('next')}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="space-y-2 mt-4">
        {/* Past days in collapsible section */}
        {pastDays.length > 0 && (
          <Collapsible defaultOpen={false}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 mt-2 border border-slate-200/40 rounded-lg bg-slate-50/80 hover:bg-slate-100/80 transition-colors group">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-muted-foreground">Voir les journées passées</h3>
                {pastActivitiesCount > 0 && (
                  <div className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                    {pastActivitiesCount}
                  </div>
                )}
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-2">
                {pastDays.map((day, index) => {
                  const hasItems = day.items.length > 0;
                  const hasConflicts = day.conflicts.length > 0;
                  
                  return (
                    <div
                      key={index}
                      className="w-full p-2 rounded-lg border border-panel bg-muted/20 hover:bg-muted/40 cursor-pointer transition-all opacity-75"
                      onClick={() => {
                        console.log('Open day view for', day.date);
                      }}
                    >
                      {/* Day header */}
                      <div className={`flex items-center justify-between ${hasItems ? 'mb-2' : 'mb-0'}`}>
                        <div className="flex items-center gap-2">
                          <div className="text-center">
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">
                              {format(day.date, 'EEE', { locale: fr })}
                            </div>
                            <div className="text-lg font-bold mt-0.5 text-muted-foreground">
                              {format(day.date, 'd')}
                            </div>
                          </div>
                          <div className="h-6 w-px bg-border" />
                          <div className="text-xs text-muted-foreground">
                            {format(day.date, 'MMMM yyyy', { locale: fr })}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!hasItems && (
                            <span className="text-xs text-muted-foreground/60 text-right">
                              aucune<br />activité
                            </span>
                          )}
                          {day.totalPriority > 0 && (
                            <PriorityIndicator 
                              priority={Math.min(Math.ceil(day.totalPriority / 3), 5)} 
                              size="sm" 
                            />
                          )}
                        </div>
                      </div>
                      {/* Items display */}
                      {hasItems && (
                        <div className="space-y-2">
                           {(() => {
                             // Group events with their tasks
                             const eventGroups = groupEventsWithTasks(day.items, tasks, taskLists);
                             const independentTasks = day.items.filter(item => 
                               item.type === 'task' && !eventGroups.some(g => g.tasks.some(t => t.id === item.id))
                             );

                            return (
                              <>
                                {/* Events with their tasks */}
                                {eventGroups.map(({ event, tasks }) => (
                                  <div key={event.id} className="space-y-1">
                                    {/* Event */}
                                    <div
                                      className={`text-xs p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-sm bg-blue-100/70 text-blue-700 hover:bg-blue-200/70 ${event.hasConflict ? 'ring-1 ring-red-300' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Extraire l'ID original pour les événements multi-jours
                                        const originalId = event.id.includes('-2024-') || event.id.includes('-2025-') || event.id.includes('-2026-') ? 
                                          event.id.split('-')[0] + '-' + event.id.split('-')[1] + '-' + event.id.split('-')[2] + '-' + event.id.split('-')[3] + '-' + event.id.split('-')[4] :
                                          event.id;
                                        openModal('viewEvent', { id: originalId });
                                      }}
                                    >
                                      <div className="flex-shrink-0">
                                        <Calendar className="h-3 w-3" />
                                      </div>
                                      <span className="font-medium flex-grow leading-tight truncate">{event.title}</span>
                                      {event.time && (
                                        <div className="flex items-center gap-1 text-xs">
                                          <Clock className="h-2 w-2" />
                                          {event.time}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Related Tasks */}
                                    {tasks.length > 0 && (
                                      <div className="ml-4 space-y-1 relative">
                                        {/* Connecting line */}
                                        <div className="absolute -left-2 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                                        {tasks.map((task) => (
                                          <div
                                            key={task.id}
                                            className={`text-xs p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-sm relative ${
                                              task.isOverdue
                                                ? 'bg-red-100/70 text-red-700 hover:bg-red-200/70'
                                                : 'bg-green-100/70 text-green-700 hover:bg-green-200/70'
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openModal('viewTask', { id: task.id });
                                            }}
                                          >
                                            <div className="flex-shrink-0">
                                              <CheckSquare className="h-3 w-3" />
                                            </div>
                                            <span className="font-medium flex-grow leading-tight truncate">{task.title}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                                {/* Independent tasks */}
                                {independentTasks.map((item) => (
                                  <div
                                    key={item.id}
                                    className={`text-xs p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-sm ${
                                      item.isOverdue
                                        ? 'bg-red-100/70 text-red-700 hover:bg-red-200/70'
                                        : 'bg-green-100/70 text-green-700 hover:bg-green-200/70'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal('viewTask', { id: item.id });
                                    }}
                                  >
                                    <div className="flex-shrink-0">
                                      <CheckSquare className="h-3 w-3" />
                                    </div>
                                    <span className="font-medium flex-grow leading-tight truncate">{item.title}</span>
                                  </div>
                                ))}
                                
                                {hasConflicts && (
                                  <div className="text-xs text-red-600 text-center py-1 font-medium flex items-center justify-center gap-1 bg-red-100/50 rounded-md border border-red-200">
                                    <AlertTriangle className="h-3 w-3" />
                                    Conflit d'horaires !
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Today's data */}
        {todayData && (
          <div
            key="today"
            className="w-full p-3 rounded-lg border border-panel bg-panel hover:bg-panel-hover cursor-pointer transition-all"
            onClick={() => {
              openModal('createEvent', { 
                defaultDate: format(todayData.date, 'yyyy-MM-dd'),
                defaultTime: '10:00'
              });
            }}
          >
            {/* Day header */}
            <div className={`flex items-center justify-between ${todayData.items.length > 0 ? 'mb-3' : 'mb-0'}`}>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">
                    {format(todayData.date, 'EEE', { locale: fr })}
                  </div>
                  <div className={`text-xl font-bold mt-1 ${
                    isToday(todayData.date) ? 'text-[#1a1c1f]' : 'text-foreground'
                  }`}>
                    {format(todayData.date, 'd')}
                  </div>
                </div>
                <div className="h-8 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <div className="text-sm text-muted-foreground">
                    {format(todayData.date, 'MMMM yyyy', { locale: fr })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {todayData.items.length === 0 && (
                  <span className="text-xs text-muted-foreground/60 text-right">
                    aucune<br />activité
                  </span>
                )}
                {todayData.totalPriority > 0 && !isToday(todayData.date) && (
                  <PriorityIndicator 
                    priority={Math.min(Math.ceil(todayData.totalPriority / 3), 5)} 
                    size="sm" 
                  />
                )}
              </div>
            </div>

            {/* Items display */}
            {isToday(todayData.date) ? (
              todayData.items.length > 0 ? (
                <div className="space-y-3">
                  {(() => {
                     // Group events with their tasks
                     const eventGroups = groupEventsWithTasks(todayData.items, tasks, taskLists);
                     const independentTasks = todayData.items.filter(item => 
                       item.type === 'task' && !eventGroups.some(g => g.tasks.some(t => t.id === item.id))
                     );

                    return (
                      <>
                        {/* Events with their tasks */}
                        {eventGroups.map(({ event, tasks }) => (
                          <div key={event.id} className="space-y-1">
                            {/* Event */}
                            <div
                              className={`text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md bg-blue-100 text-blue-800 hover:bg-blue-200 ${event.hasConflict ? 'ring-2 ring-red-300' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Extraire l'ID original pour les événements multi-jours
                                const originalId = event.id.includes('-2024-') || event.id.includes('-2025-') || event.id.includes('-2026-') ? 
                                  event.id.split('-')[0] + '-' + event.id.split('-')[1] + '-' + event.id.split('-')[2] + '-' + event.id.split('-')[3] + '-' + event.id.split('-')[4] :
                                  event.id;
                                openModal('viewEvent', { id: originalId });
                              }}
                            >
                              <div className="flex-shrink-0">
                                <Calendar className="h-4 w-4" />
                              </div>
                              <span className="font-medium flex-grow leading-tight">{event.title}</span>
                              {event.time && (
                                <div className="flex items-center gap-1 text-xs">
                                  <Clock className="h-3 w-3" />
                                  {event.time}
                                </div>
                              )}
                            </div>
                            
                            {/* Related Tasks */}
                            {tasks.length > 0 && (
                              <div className="ml-6 space-y-1 relative">
                                {/* Connecting line */}
                                 <div className="absolute -left-3 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                                {tasks.map((task) => (
                                  <div
                                    key={task.id}
                                     className="text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md relative bg-green-100 text-green-800 hover:bg-green-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal('viewTask', { id: task.id });
                                    }}
                                  >
                                    <div className="flex-shrink-0">
                                      <CheckSquare className="h-4 w-4" />
                                    </div>
                                    <span className="font-medium flex-grow leading-tight">{task.title}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Independent tasks */}
                        {independentTasks.map((item) => (
                          <div
                            key={item.id}
                             className="text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md bg-green-100 text-green-800 hover:bg-green-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal('viewTask', { id: item.id });
                            }}
                          >
                            <div className="flex-shrink-0">
                              <CheckSquare className="h-4 w-4" />
                            </div>
                            <span className="font-medium flex-grow leading-tight">{item.title}</span>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              ) : null
            ) : null}
          </div>
        )}

        {/* Future days */}
        {futureDays.map((day, index) => {
          const hasItems = day.items.length > 0;
          const hasConflicts = day.conflicts.length > 0;
          
          return (
            <div
              key={`future-${index}`}
              className="w-full p-3 rounded-lg border border-panel bg-panel hover:bg-panel-hover cursor-pointer transition-all"
              onClick={() => {
                openModal('createEvent', { 
                  defaultDate: format(day.date, 'yyyy-MM-dd'),
                  defaultTime: '10:00'
                });
              }}
            >
              {/* Day header */}
              <div className={`flex items-center justify-between ${hasItems ? 'mb-3' : 'mb-0'}`}>
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">
                      {format(day.date, 'EEE', { locale: fr })}
                    </div>
                    <div className="text-xl font-bold mt-1 text-foreground">
                      {format(day.date, 'd')}
                    </div>
                  </div>
                  <div className="h-8 w-px bg-border" />
                  <div className="text-sm text-muted-foreground">
                    {format(day.date, 'MMMM yyyy', { locale: fr })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!hasItems && (
                    <span className="text-xs text-muted-foreground/60 text-right">
                      aucune<br />activité
                    </span>
                  )}
                </div>
              </div>

              {/* Items display */}
              {hasItems && (
                <div className="space-y-3">
                  {(() => {
                     // Group events with their tasks
                     const eventGroups = groupEventsWithTasks(day.items, tasks, taskLists);
                     const independentTasks = day.items.filter(item => 
                       item.type === 'task' && !eventGroups.some(g => g.tasks.some(t => t.id === item.id))
                     );

                    return (
                      <>
                        {/* Events with their tasks */}
                        {eventGroups.map(({ event, tasks }) => (
                          <div key={event.id} className="space-y-1">
                            {/* Event */}
                            <div
                              className={`text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md bg-blue-100 text-blue-800 hover:bg-blue-200 ${event.hasConflict ? 'ring-2 ring-red-300' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Extraire l'ID original pour les événements multi-jours
                                const originalId = event.id.includes('-2024-') || event.id.includes('-2025-') || event.id.includes('-2026-') ? 
                                  event.id.split('-')[0] + '-' + event.id.split('-')[1] + '-' + event.id.split('-')[2] + '-' + event.id.split('-')[3] + '-' + event.id.split('-')[4] :
                                  event.id;
                                openModal('viewEvent', { id: originalId });
                              }}
                            >
                              <div className="flex-shrink-0">
                                <Calendar className="h-4 w-4" />
                              </div>
                              <span className="font-medium flex-grow leading-tight">{event.title}</span>
                              {event.time && (
                                <div className="flex items-center gap-1 text-xs">
                                  <Clock className="h-3 w-3" />
                                  {event.time}
                                </div>
                              )}
                            </div>
                            
                            {/* Related Tasks */}
                            {tasks.length > 0 && (
                              <div className="ml-6 space-y-1 relative">
                                {/* Connecting line */}
                                <div className="absolute -left-3 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                                {tasks.map((task) => (
                                  <div
                                    key={task.id}
                                     className="text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md relative bg-green-100 text-green-800 hover:bg-green-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal('viewTask', { id: task.id });
                                    }}
                                  >
                                    <div className="flex-shrink-0">
                                      <CheckSquare className="h-4 w-4" />
                                    </div>
                                    <span className="font-medium flex-grow leading-tight">{task.title}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Independent tasks */}
                        {independentTasks.map((item) => (
                          <div
                            key={item.id}
                             className="text-sm p-2 rounded-md flex items-center gap-2 transition-all hover:shadow-md bg-green-100 text-green-800 hover:bg-green-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal('viewTask', { id: item.id });
                            }}
                          >
                            <div className="flex-shrink-0">
                              <CheckSquare className="h-4 w-4" />
                            </div>
                            <span className="font-medium flex-grow leading-tight">{item.title}</span>
                          </div>
                        ))}
                        
                        {hasConflicts && (
                          <div className="text-sm text-red-600 text-center py-2 font-medium flex items-center justify-center gap-2 bg-red-100/50 rounded-md border border-red-200">
                            <AlertTriangle className="h-4 w-4" />
                            Conflit d'horaires !
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Function to group events with their related tasks
function groupEventsWithTasks(dayItems: WeekItem[], allTasks: any[], taskLists: any[] = []): { event: WeekItem; tasks: WeekItem[] }[] {
  const events = dayItems.filter(item => item.type === 'event');
  return events.map(event => {
    // Get task lists associated with this event
    const eventTaskLists = taskLists.filter(list => (list as any).event_id === event.id);
    const eventTaskListIds = eventTaskLists.map(list => list.id);
    
    const relatedTasks = (allTasks || [])
      .filter(t => {
        // Include tasks directly linked to event OR tasks in event-linked task lists
        return (t.event_id === event.id || (t.list_id && eventTaskListIds.includes(t.list_id))) 
               && t.status !== 'completed';
      })
      .map((t) => {
        const taskDate = t.due_date ? new Date(t.due_date + 'T00:00:00') : event.date;
        return {
          id: t.id,
          type: 'task',
          title: t.title,
          date: taskDate,
          priority: calculateTaskPriority(t, taskDate),
          isOverdue: t.due_date ? isBefore(taskDate, new Date()) : false,
          assignedTo: t.assigned_to_list || [t.assigned_to],
          event_id: t.event_id
        } as WeekItem;
      });
    return { event, tasks: relatedTasks };
  });
}

function detectTimeConflicts(items: WeekItem[]): WeekItem[][] {
  const conflicts: WeekItem[][] = [];
  const eventsWithTime = items.filter(item => item.type === 'event' && item.time);
  
  for (let i = 0; i < eventsWithTime.length; i++) {
    for (let j = i + 1; j < eventsWithTime.length; j++) {
      const item1 = eventsWithTime[i];
      const item2 = eventsWithTime[j];
      
      if (item1.time === item2.time) {
        item1.hasConflict = true;
        item2.hasConflict = true;
        conflicts.push([item1, item2]);
      }
    }
  }
  
  return conflicts;
}

function calculateEventPriority(event: any, eventDate: Date): number {
  let priority = 1;
  
  // More attendees = higher priority
  if (event.linked_persons?.length > 3) priority += 2;
  else if (event.linked_persons?.length > 1) priority += 1;
  
  // Location makes it more important
  if (event.location) priority += 1;
  
  // Recurring events are more important
  if (event.recurrence_type !== 'none') priority += 1;
  
  return Math.min(priority, 5);
}

function calculateTaskPriority(task: any, taskDate: Date): number {
  let priority = 1;
  
  // Overdue tasks get max priority
  if (isBefore(taskDate, new Date())) return 5;
  
  // Multiple assignees = higher priority
  if (task.assigned_to_list?.length > 1) priority += 1;
  
  // Task complexity/description suggests importance
  if (task.description && task.description.length > 50) priority += 1;
  
  return Math.min(priority, 5);
}